import { useEffect, useRef } from 'react';
import type { CheckRun } from '@fkwd-qc/shared';
import { useCheckRuns } from '@/hooks/use-check-runs';
import { useDrawer } from '@/hooks/use-drawer';
import { useFocusTrap } from '@/hooks/use-focus-trap';
import { useNow } from '@/hooks/use-now';
import { useShellStatus } from '@/hooks/use-shell-status';
import { formatClock, formatRelativeAge } from '@/lib/format';
import { statusTone } from '@/lib/status';
import { LoadingRows } from './loading-rows';
import { StatusDot } from './status-dot';
import { StatusPill } from './status-pill';

/**
 * What the drawer heading shows before a target is known.
 *
 * @since 1.3.0
 */
const HEADING_PLACEHOLDER = '-';

interface EvidenceBlockProps {
    run: CheckRun;
}

/**
 * Renders the evidence map exactly as the check recorded it: one key and value per line.
 *
 * @since 1.0.0
 * @param props - the run whose evidence should be listed
 * @returns the evidence block element
 */
function EvidenceBlock({ run }: EvidenceBlockProps) {
    // a run written without evidence still opens rather than failing the drawer
    const entries = Object.entries(run.evidence ?? {});

    if (entries.length === 0) {
        return <div className="fkwd-evidence">no evidence recorded</div>;
    }

    return (
        <div className="fkwd-evidence">
            {entries.map(([key, value], index) => (
                <span key={key}>
                    {index === 0 ? null : '\n'}
                    <span className="fkwd-evidence__key">{key}</span>
                    {'  '}
                    {value === null ? 'none' : String(value)}
                </span>
            ))}
        </div>
    );
}

/**
 * The right-hand drawer that shows why a check reached its verdict: the outcome, the
 * summary, the raw evidence, recent runs and when it last ran.
 *
 * @since 1.0.0
 * @returns the drawer and its scrim
 */
export function EvidenceDrawer() {
    const { target, isOpen, closeEvidence } = useDrawer();
    const { resolveCheckName, resolveClientName } = useShellStatus();
    const runsQuery = useCheckRuns(target?.clientSlug, target?.checkSlug);
    const now = useNow();

    const drawerRef = useRef<HTMLElement>(null);
    const closeRef = useRef<HTMLButtonElement>(null);

    useFocusTrap(drawerRef, isOpen, closeEvidence);

    useEffect(() => {
        if (isOpen) {
            closeRef.current?.focus();
        }
    }, [isOpen]);

    const rows = runsQuery.data?.rows ?? [];
    const latest = rows[0];
    const history = rows.slice(1);
    const clientName = target === null ? '' : resolveClientName(target.clientSlug);
    const checkName = target === null ? '' : resolveCheckName(target.checkSlug);

    return (
        <>
            <div
                aria-hidden="true"
                className={isOpen ? 'fkwd-scrim fkwd-scrim--open' : 'fkwd-scrim'}
                onClick={closeEvidence}
            />
            <aside
                aria-label={`${checkName} evidence`}
                aria-modal="true"
                className={isOpen ? 'fkwd-drawer fkwd-drawer--open' : 'fkwd-drawer'}
                ref={drawerRef}
                role="dialog"
            >
                <div className="fkwd-drawer-head">
                    <div>
                        <div className="fkwd-drawer-head__eyebrow">{clientName || HEADING_PLACEHOLDER}</div>
                        <h2>{checkName || HEADING_PLACEHOLDER}</h2>
                    </div>
                    <button
                        aria-label="Close"
                        className="fkwd-drawer-head__close"
                        onClick={closeEvidence}
                        ref={closeRef}
                        type="button"
                    >
                        ×
                    </button>
                </div>
                <div className="fkwd-drawer-body">
                    {runsQuery.isPending && target !== null ? <LoadingRows label="Loading evidence" /> : null}

                    {runsQuery.isError ? (
                        <p className="fkwd-summary">Evidence could not be loaded. Close this and try again.</p>
                    ) : null}

                    {latest === undefined || runsQuery.isPending ? null : (
                        <>
                            <StatusPill label={latest.status} tone={statusTone(latest.status)} />
                            <p className="fkwd-summary">{latest.summary}</p>

                            <div className="fkwd-label">Evidence</div>
                            <EvidenceBlock run={latest} />

                            <div className="fkwd-label">Recent runs</div>
                            {history.length === 0 ? (
                                <div className="fkwd-history-row">
                                    <span className="fkwd-history-row__time">{HEADING_PLACEHOLDER}</span>
                                    <span>No earlier runs recorded</span>
                                </div>
                            ) : (
                                history.map((run) => (
                                    <div className="fkwd-history-row" key={run.qcRunId}>
                                        <StatusDot isSmall status={run.status} />
                                        <span className="fkwd-history-row__time">
                                            {formatClock(run.finishedAt ?? run.startedAt)}
                                        </span>
                                        <span>{run.summary}</span>
                                    </div>
                                ))
                            )}

                            <div className="fkwd-label fkwd-label--spaced">Last run</div>
                            <div className="fkwd-drawer__meta">
                                {formatRelativeAge(latest.finishedAt ?? latest.startedAt, now)} ago
                            </div>
                        </>
                    )}

                    {latest === undefined && !runsQuery.isPending && !runsQuery.isError ? (
                        <p className="fkwd-summary">This check has not run for this client yet.</p>
                    ) : null}
                </div>
            </aside>
        </>
    );
}
